import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Upload, ChevronRight, ChevronLeft, Users, Lock, Globe, Search, Trash2, Plus, Loader2, Instagram, Facebook, Twitter, Youtube, Linkedin } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { uploadImage } from '../../lib/storage';
import { generateSlug } from '../../lib/utils/slugUtils';
import { LocationPicker } from '../LocationPicker';
import { Avatar } from '../Profile/Avatar';
import type { CommunitySocialLinks } from '../../types/communities';

// TikTok icon
function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.73a8.19 8.19 0 004.76 1.52V6.8a4.84 4.84 0 01-1-.11z" />
    </svg>
  );
}

const SOCIAL_FIELDS: { key: keyof CommunitySocialLinks; label: string; icon: React.ReactNode; placeholder: string }[] = [
  { key: 'website', label: 'Website', icon: <Globe className="h-4 w-4 text-gray-500" />, placeholder: 'https://yourwebsite.com' },
  { key: 'instagram', label: 'Instagram', icon: <Instagram className="h-4 w-4 text-pink-500" />, placeholder: 'username or full URL' },
  { key: 'facebook', label: 'Facebook', icon: <Facebook className="h-4 w-4 text-blue-600" />, placeholder: 'page name or full URL' },
  { key: 'twitter', label: 'X / Twitter', icon: <Twitter className="h-4 w-4 text-gray-800" />, placeholder: 'username or full URL' },
  { key: 'youtube', label: 'YouTube', icon: <Youtube className="h-4 w-4 text-red-500" />, placeholder: 'channel name or full URL' },
  { key: 'tiktok', label: 'TikTok', icon: <TikTokIcon className="h-4 w-4 text-gray-800" />, placeholder: 'username or full URL' },
  { key: 'linkedin', label: 'LinkedIn', icon: <Linkedin className="h-4 w-4 text-blue-700" />, placeholder: 'company slug or full URL' },
];

interface Practitioner {
  user_id: string;
  username: string;
  full_name?: string;
  avatar_url?: string;
  bio?: string;
}

interface CreateCommunityModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export function CreateCommunityModal({ onClose, onSuccess }: CreateCommunityModalProps) {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'public' as 'public' | 'private',
    latitude: null as number | null,
    longitude: null as number | null,
    about_text: ''
  });
  const [practitioners, setPractitioners] = useState<Practitioner[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [avatar, setAvatar] = useState<File | null>(null);
  const [banner, setBanner] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [aboutImages, setAboutImages] = useState<string[]>([]);
  const [uploadingAboutImage, setUploadingAboutImage] = useState(false);
  const [socialLinks, setSocialLinks] = useState<CommunitySocialLinks>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const avatarInputRef = React.useRef<HTMLInputElement>(null);
  const bannerInputRef = React.useRef<HTMLInputElement>(null);
  const MAX_ABOUT_IMAGES = 5;

  // Search users for practitioners
  useEffect(() => {
    const searchUsers = async () => {
      if (searchQuery.length < 2) {
        setSearchResults([]);
        return;
      }

      setSearchLoading(true);
      try {
        const { data, error } = await supabase
          .from('users')
          .select('id, username, full_name, avatar_url')
          .or(`username.ilike.%${searchQuery}%,full_name.ilike.%${searchQuery}%`)
          .limit(10);

        if (error) throw error;
        // Filter out already added practitioners
        const filtered = (data || []).filter(
          user => !practitioners.some(p => p.user_id === user.id)
        );
        setSearchResults(filtered);
      } catch (err) {
        console.error('Error searching users:', err);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    };

    const timeoutId = setTimeout(searchUsers, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, practitioners]);

  const addPractitioner = (user: any) => {
    const newPractitioner: Practitioner = {
      user_id: user.id,
      username: user.username,
      full_name: user.full_name,
      avatar_url: user.avatar_url,
      bio: ''
    };
    setPractitioners([...practitioners, newPractitioner]);
    setSearchQuery('');
    setSearchResults([]);
  };

  const removePractitioner = (userId: string) => {
    setPractitioners(practitioners.filter(p => p.user_id !== userId));
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatar(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBannerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setBanner(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setBannerPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAboutImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const remainingSlots = MAX_ABOUT_IMAGES - aboutImages.length;
    if (remainingSlots <= 0) return;
    const filesToUpload = Array.from(files).slice(0, remainingSlots);
    setUploadingAboutImage(true);
    try {
      const urls: string[] = [];
      for (const file of filesToUpload) {
        const url = await uploadImage(file, 'community-images');
        if (url) urls.push(url);
      }
      setAboutImages(prev => [...prev, ...urls]);
    } catch (err: any) {
      setError(err.message || 'Failed to upload image');
    } finally {
      setUploadingAboutImage(false);
      e.target.value = '';
    }
  };

  const removeAboutImage = (index: number) => {
    setAboutImages(prev => prev.filter((_, i) => i !== index));
  };

  const updateSocialLink = (key: keyof CommunitySocialLinks, value: string) => {
    setSocialLinks(prev => {
      const updated = { ...prev };
      if (value.trim()) {
        updated[key] = value.trim();
      } else {
        delete updated[key];
      }
      return updated;
    });
  };

  const TOTAL_STEPS = 5;

  const canProceedToNextStep = () => {
    switch (currentStep) {
      case 1:
        return formData.type !== '';
      case 2:
        return formData.name.trim() !== '' && formData.description.trim() !== '';
      case 3:
        return true; // Media is optional
      case 4:
        return true; // Content (about/practitioners) is optional
      case 5:
        return true; // Images & social links are optional
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (canProceedToNextStep() && currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Generate slug from name with collision handling
      let slug = generateSlug(formData.name);
      let slugAttempts = 0;
      while (slugAttempts < 5) {
        const { data: existingCommunity } = await supabase
          .from('communities')
          .select('id')
          .eq('slug', slug)
          .maybeSingle();

        if (!existingCommunity) break;

        slugAttempts++;
        slug = `${generateSlug(formData.name)}-${slugAttempts}`;
      }

      if (slugAttempts >= 5) {
        throw new Error('A community with a very similar name already exists. Please choose a more unique name.');
      }

      // Upload images if provided
      let avatarUrl = '';
      let bannerUrl = '';

      if (avatar) {
        const { data: avatarData, error: avatarError } = await supabase.storage
          .from('community-images')
          .upload(`${user.id}/${Date.now()}-avatar`, avatar);

        if (avatarError) throw avatarError;

        const { data: { publicUrl } } = supabase.storage
          .from('community-images')
          .getPublicUrl(avatarData.path);

        avatarUrl = publicUrl;
      }

      if (banner) {
        const { data: bannerData, error: bannerError } = await supabase.storage
          .from('community-images')
          .upload(`${user.id}/${Date.now()}-banner`, banner);

        if (bannerError) throw bannerError;

        const { data: { publicUrl } } = supabase.storage
          .from('community-images')
          .getPublicUrl(bannerData.path);

        bannerUrl = publicUrl;
      }

      // Create community
      const { data: community, error: insertError } = await supabase
        .from('communities')
        .insert([{
          name: formData.name,
          slug: slug,
          description: formData.description,
          type: formData.type,
          owner_id: user.id,
          avatar_url: avatarUrl,
          banner_url: bannerUrl,
          latitude: formData.latitude,
          longitude: formData.longitude,
          about_text: formData.about_text || null,
          about_images: aboutImages.length > 0 ? aboutImages : null,
          social_links: Object.keys(socialLinks).length > 0 ? socialLinks : null,
          practitioners: practitioners.length > 0 ? practitioners : []
        }])
        .select()
        .single();

      if (insertError) {
        // Check if it's a unique constraint violation
        if (insertError.code === '23505') {
          throw new Error('A community with this name already exists. Please choose a different name.');
        }
        throw insertError;
      }

      // Add creator as admin member
      const { error: memberError } = await supabase
        .from('community_members')
        .insert([{
          community_id: community.id,
          user_id: user.id,
          role: 'admin'
        }]);

      if (memberError) throw memberError;
      
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-0 lg:p-4">
      <div className="bg-background rounded-none lg:rounded-lg w-full h-full lg:h-auto lg:max-w-2xl lg:max-h-[90vh] overflow-hidden flex flex-col lg:shadow-2xl">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-semibold text-content">
              {currentStep === 1 && t('forms.community.chooseType')}
              {currentStep === 2 && t('forms.community.details')}
              {currentStep === 3 && t('forms.community.addMedia')}
              {currentStep === 4 && t('forms.community.content')}
              {currentStep === 5 && 'Gallery & Social Links'}
            </h2>
            <p className="text-sm text-content/60 mt-1">{t('forms.common.stepOf', { current: currentStep, total: TOTAL_STEPS })}</p>
          </div>
          <button onClick={onClose} className="text-content/60 hover:text-content">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4 bg-gray-50">
          <div className="flex gap-2">
            {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((step) => (
              <div
                key={step}
                className={`flex-1 h-2 rounded-full transition-colors ${
                  step <= currentStep ? 'bg-accent-text' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-md text-sm">{error}</div>
          )}

          {/* Step 1: Community Type */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <p className="text-content/70">{t('forms.community.selectType')}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, type: 'public' }))}
                  className={`p-6 text-left border-2 rounded-lg transition-all ${
                    formData.type === 'public'
                      ? 'border-accent-text bg-accent-base/10 shadow-sm'
                      : 'border-gray-200 hover:border-accent-text/50 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`p-2 rounded-lg ${
                      formData.type === 'public' ? 'bg-accent-text text-white' : 'bg-gray-100 text-gray-600'
                    }`}>
                      <Globe className="w-5 h-5" />
                    </div>
                    <h3 className="text-lg font-semibold text-content">{t('forms.community.publicCommunity')}</h3>
                  </div>
                  <p className="text-sm text-content/70">
                    {t('forms.community.publicDesc')}
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, type: 'private' }))}
                  className={`p-6 text-left border-2 rounded-lg transition-all ${
                    formData.type === 'private'
                      ? 'border-accent-text bg-accent-base/10 shadow-sm'
                      : 'border-gray-200 hover:border-accent-text/50 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`p-2 rounded-lg ${
                      formData.type === 'private' ? 'bg-accent-text text-white' : 'bg-gray-100 text-gray-600'
                    }`}>
                      <Lock className="w-5 h-5" />
                    </div>
                    <h3 className="text-lg font-semibold text-content">{t('forms.community.privateCommunity')}</h3>
                  </div>
                  <p className="text-sm text-content/70">
                    {t('forms.community.privateDesc')}
                  </p>
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Details */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-content mb-2">
                  {t('forms.community.communityName')}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-text focus:ring-2 focus:ring-accent-text/20 bg-background"
                  placeholder={t('forms.community.namePlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-content mb-2">
                  {t('forms.community.description')}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={4}
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-text focus:ring-2 focus:ring-accent-text/20 bg-background resize-none"
                  placeholder={t('forms.community.descriptionPlaceholder')}
                />
                <p className="mt-2 text-xs text-content/60">
                  {t('forms.community.descriptionHelper')}
                </p>
              </div>

              <LocationPicker
                latitude={formData.latitude}
                longitude={formData.longitude}
                onChange={(lat, lng) => setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }))}
                helperText={t('forms.community.locationHelper')}
              />
            </div>
          )}

          {/* Step 3: Media */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <p className="text-content/70">{t('forms.community.addMediaInfo')}</p>
              
              <div>
                <label className="block text-sm font-medium text-content mb-3">
                  {t('forms.community.communityAvatar')}
                </label>
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  className="hidden"
                />
                <div
                  onClick={() => avatarInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-accent-text transition-colors"
                >
                  {avatarPreview ? (
                    <div className="relative inline-block">
                      <img src={avatarPreview} alt="Avatar preview" className="w-32 h-32 rounded-full object-cover" />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setAvatarPreview(null);
                          setAvatar(null);
                        }}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div>
                      <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-content/60">{t('forms.community.clickUploadAvatar')}</p>
                      <p className="text-sm text-content/40 mt-1">{t('forms.community.squareImage')}</p>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-content mb-3">
                  {t('forms.community.communityBanner')}
                </label>
                <input
                  ref={bannerInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleBannerChange}
                  className="hidden"
                />
                <div
                  onClick={() => bannerInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-accent-text transition-colors"
                >
                  {bannerPreview ? (
                    <div className="relative">
                      <img src={bannerPreview} alt="Banner preview" className="w-full h-64 object-cover rounded-lg" />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setBannerPreview(null);
                          setBanner(null);
                        }}
                        className="absolute top-2 right-2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div>
                      <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-content/60">{t('forms.community.clickUploadBanner')}</p>
                      <p className="text-sm text-content/40 mt-1">{t('forms.community.wideImage')}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Community Content */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <p className="text-content/70">
                {t('forms.community.contentInfo')}
              </p>

              {/* About Text */}
              <div>
                <label className="block text-sm font-medium text-content mb-2">
                  {t('forms.community.about')}
                </label>
                <p className="text-xs text-content/50 mb-3">
                  {t('forms.community.aboutHelper')}
                </p>
                <textarea
                  value={formData.about_text}
                  onChange={(e) => setFormData(prev => ({ ...prev, about_text: e.target.value }))}
                  rows={5}
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-text focus:ring-2 focus:ring-accent-text/20 bg-background resize-none"
                  placeholder={t('forms.community.aboutPlaceholder')}
                />
              </div>

              {/* Practitioners */}
              <div>
                <label className="block text-sm font-medium text-content mb-2">
                  {t('forms.community.practitioners')}
                </label>
                <p className="text-xs text-content/50 mb-3">
                  {t('forms.community.practitionersHelper')}
                </p>

                {/* Search for users */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t('forms.community.searchUsers')}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-text/20 focus:border-accent-text"
                  />
                </div>

                {/* Search Results */}
                {searchQuery.length >= 2 && (
                  <div className="mb-4 max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
                    {searchLoading ? (
                      <div className="text-center py-3 text-content/60">{t('forms.community.searching')}</div>
                    ) : searchResults.length > 0 ? (
                      <div>
                        {searchResults.map((user) => (
                          <button
                            key={user.id}
                            type="button"
                            onClick={() => addPractitioner(user)}
                            className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 text-left"
                          >
                            <Avatar
                              url={user.avatar_url}
                              size="sm"
                              userId={user.id}
                              editable={false}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-content truncate">
                                {user.full_name || user.username}
                              </div>
                              <div className="text-sm text-content/60">@{user.username}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-3 text-content/60">{t('forms.community.noUsersFound')}</div>
                    )}
                  </div>
                )}

                {/* Added Practitioners */}
                {practitioners.length > 0 && (
                  <div className="space-y-2">
                    {practitioners.map((practitioner) => (
                      <div
                        key={practitioner.user_id}
                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                      >
                        <Avatar
                          url={practitioner.avatar_url}
                          size="sm"
                          userId={practitioner.user_id}
                          editable={false}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-content truncate">
                            {practitioner.full_name || practitioner.username}
                          </div>
                          <div className="text-sm text-content/60">@{practitioner.username}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removePractitioner(practitioner.user_id)}
                          className="p-1 text-red-500 hover:text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {practitioners.length === 0 && (
                  <p className="text-sm text-content/50 text-center py-4">
                    {t('forms.community.noPractitionersAdded')}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Step 5: Gallery Images & Social Links */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <p className="text-content/70">
                Add images and social media links to your community's About section. You can always update these later.
              </p>

              {/* Gallery Images */}
              <div>
                <label className="block text-sm font-semibold text-content mb-2">
                  Gallery Images
                  <span className="text-xs font-normal text-content/50 ml-2">
                    ({aboutImages.length}/{MAX_ABOUT_IMAGES})
                  </span>
                </label>
                <p className="text-xs text-content/50 mb-3">
                  Add up to {MAX_ABOUT_IMAGES} images to showcase your community.
                </p>

                <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                  {aboutImages.map((url, idx) => (
                    <div key={idx} className="relative aspect-square rounded-xl overflow-hidden group border border-gray-200">
                      <img src={url} alt={`Gallery ${idx + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeAboutImage(idx)}
                        className="absolute top-1.5 right-1.5 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-sm hover:bg-red-600"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}

                  {aboutImages.length < MAX_ABOUT_IMAGES && (
                    <label className={`aspect-square rounded-xl border-2 border-dashed border-gray-300 hover:border-accent-text/50 flex flex-col items-center justify-center cursor-pointer transition-colors ${uploadingAboutImage ? 'opacity-50 pointer-events-none' : ''}`}>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleAboutImageUpload}
                        className="hidden"
                        disabled={uploadingAboutImage}
                      />
                      {uploadingAboutImage ? (
                        <Loader2 className="h-5 w-5 text-accent-text animate-spin" />
                      ) : (
                        <>
                          <Plus className="h-5 w-5 text-gray-400 mb-1" />
                          <span className="text-[10px] text-gray-400 font-medium">Add</span>
                        </>
                      )}
                    </label>
                  )}
                </div>
              </div>

              {/* Social Media Links */}
              <div>
                <label className="block text-sm font-semibold text-content mb-2">
                  Social Media Links
                </label>
                <p className="text-xs text-content/50 mb-3">
                  Add links to your community's social media profiles.
                </p>
                <div className="space-y-2.5">
                  {SOCIAL_FIELDS.map(field => (
                    <div key={field.key} className="flex items-center gap-2.5">
                      <div className="w-9 h-9 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-center flex-shrink-0">
                        {field.icon}
                      </div>
                      <input
                        type="text"
                        value={socialLinks[field.key] || ''}
                        onChange={(e) => updateSocialLink(field.key, e.target.value)}
                        placeholder={field.placeholder}
                        className="flex-1 px-3.5 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-accent-text/20 focus:border-accent-text transition-colors placeholder:text-content/25"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 flex justify-between items-center">
          <button
            type="button"
            onClick={prevStep}
            disabled={currentStep === 1}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              currentStep === 1
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-content hover:bg-gray-100'
            }`}
          >
            <ChevronLeft className="w-4 h-4" />
            {t('forms.common.previous')}
          </button>

          {currentStep < TOTAL_STEPS ? (
            <button
              type="button"
              onClick={nextStep}
              disabled={!canProceedToNextStep()}
              className={`flex items-center gap-2 px-6 py-2 rounded-full font-medium text-white transition-colors ${
                canProceedToNextStep()
                  ? 'bg-accent-text hover:bg-accent-text/90'
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
            >
              {t('forms.common.next')}
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || !canProceedToNextStep()}
              className={`flex items-center gap-2 px-6 py-2 rounded-full font-medium text-white transition-colors ${
                loading || !canProceedToNextStep()
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-accent-text hover:bg-accent-text/90'
              }`}
            >
              {loading ? t('forms.common.creating') : (
                <>
                  <Users className="w-4 h-4" />
                  {t('forms.community.createCommunity')}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
